/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {makeStyles} from '@material-ui/core';
import classnames from 'classnames';
import dynamic from 'next/dynamic';
import {useHasMounted} from '@ion/core/src/hooks/useHasMounted';
import Device from '@ion/core/src/services/Device';
import {
  isBottomSheetTableView,
  isSideSheetTableView
} from '@ion/dls/src/components/sidesheet/helpers';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {dlsLayout} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';
import {refreshListAssets} from '../../../store/actions/tracking';
import {
  findNearbySelector,
  largeFleetAssetRefreshSelector,
  selectedToolSelector,
  showAssetsListSelector,
  showDetailPanelSelector,
  sideSheetViewSelector
} from '../../../store/selectors/tracking-selectors';
import TagTrackedAssetsPanel from '../TagTrackedAssetsPanel';
import ResultsFiltersContainer from './ResultsFiltersContainer';
import SidesheetDetailTabs from './SidesheetDetailTabs';

const SidesheetPanelListContainerNoSSR = dynamic(() => import('./SidesheetPanelListContainer'),
  {ssr: false}
);
const FindNearByContainerNoSSR = dynamic(() => import('./FindNearByContainer'),
  {ssr: false}
);

interface SidesheetContentProps {
  assetList?: Device[];
  findNearbyBreadcrumb?: string | React.JSX.Element;
};

const useStyles = makeStyles((theme: DLSTheme) => {
  const {
    dls: {
      motion: {
        easing : {
          standard = 'ease'
        },
        duration : {
          t4 = '200ms'
        }
      }
    }
  } = theme;

  const width = `${dlsLayout('sidesheet', 'width')}px`;

  return {
    sideSheetContentWrapper : {
      transition                     : `transform ${t4} ${standard}`,
      [theme.breakpoints.down('sm')] : {
        position : 'relative',
        display  : 'block'
      }
    },
    sideSheetItem : {
      [theme.breakpoints.down('sm')] : {
        width : '100vw'
      }
    },
    panelHeaderWrapper : {
      opacity                 : 1,
      animationName           : '$fadeInEffect',
      animationIterationCount : 1,
      animationTimingFunction : 'ease-in',
      animationDuration       : '1s'
    }
  };
});

/**
 * @SidesheetContent
 * Returns component for rendering breadcrumbs, panel list, details and associated tag details.
 *
 *  ```typescript
 *   import SidesheetConteπnt from '@/routes/tracking/sidesheet/SidesheetContent';
 *
 *   <SidesheetContent />
 *  ```
 */
const SidesheetContent = ({assetList, findNearbyBreadcrumb}: SidesheetContentProps) => {
  const classes         = useStyles();
  const {isPhone}       = useMedia();
  const dispatch        = useDispatch();
  const hasMounted      = useHasMounted();
  const refreshRef      = useRef(null);
  const showAssetsList   = useSelector(showAssetsListSelector);
  const showDetailPanel  = useSelector(showDetailPanelSelector);
  const findLabel        = useSelector(findNearbySelector);
  const hfmRefresh       = useSelector(largeFleetAssetRefreshSelector);
  const sideSheetView    = useSelector(sideSheetViewSelector);
  const selectedTool     = useSelector(selectedToolSelector);

  useEffect(function() {
    if (hfmRefresh || isPhone) {
      refreshRef.current = Date.now();
      if (!isSideSheetTableView(sideSheetView) &&
        !isBottomSheetTableView(sideSheetView)) {
        dispatch(refreshListAssets() as any);
      }
    }
  }, [hfmRefresh, isPhone]);

  if (hasMounted) {
    const findNearbyOnMobile = isPhone && findLabel.asset;
    const showDetailTabs     = !findNearbyOnMobile && !showAssetsList && showDetailPanel;

    return (
      <div className={classnames(classes.sideSheetContentWrapper)}>
        <div className={classes.sideSheetItem}>
          {
            showAssetsList && (
              <div className={classes.panelHeaderWrapper}>
                <ResultsFiltersContainer findNearbyBreadcrumb={findNearbyBreadcrumb} />
                <SidesheetPanelListContainerNoSSR key={refreshRef.current}
                  assetList={assetList} />
              </div>
            )
          }
        </div>
        <div className={classes.sideSheetItem}>
          {showDetailTabs && (
            <SidesheetDetailTabs />
          )}
          <FindNearByContainerNoSSR />
        </div>
        {selectedTool && (
          <div className={classes.sideSheetItem}>
            <TagTrackedAssetsPanel />
          </div>
        )}
      </div>
    );
  }
  return null;
};
export default React.memo(SidesheetContent);
